import { OptionOnSelectData } from "@fluentui/react-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { UseDropdownWithUrlSyncOptions } from "../Interfaces/UseDropdownWithUrlSync.interfaces";
import { useUrlParamSyncWithFilters } from "./useUrlParamSyncWithFilters";

export const useDropdownWithUrlSync = <T extends string>({
  validValues,
  defaultValue,
  paramName = "vb",
  onValueChange
}: UseDropdownWithUrlSyncOptions<T>) => {
  const isInitialized = useRef(false);

  const {
    syncWithUrl,
    updateUrlParam,
    getInitialValue,
    currentValue: urlValue
  } = useUrlParamSyncWithFilters(validValues, defaultValue, paramName);

  const [selectedValue, setSelectedValue] = useState<T>(getInitialValue);

  // Handle dropdown selection
  const handleSelection = useCallback(
    ({ optionText }: OptionOnSelectData) => {
      const newValue = (optionText || defaultValue) as T;
      setSelectedValue(newValue);

      // Let updateUrlParam handle the default value logic
      updateUrlParam(newValue);

      onValueChange?.(newValue);
    },
    [updateUrlParam, onValueChange, defaultValue]
  );

  // Initialize from URL parameters on first load
  useEffect(() => {
    if (!isInitialized.current) {
      const initialValue = getInitialValue();
      if (initialValue !== selectedValue) {
        setSelectedValue(initialValue);
        onValueChange?.(initialValue);
      }
      isInitialized.current = true;
    }
  }, [getInitialValue, selectedValue, onValueChange]);

  // Sync with URL changes - Let syncWithUrl handle all the logic
  useEffect(() => {
    if (isInitialized.current) {
      const syncedValue = syncWithUrl(selectedValue);
      if (syncedValue !== selectedValue) {
        setSelectedValue(syncedValue);
        onValueChange?.(syncedValue);
      }
    }
  }, [urlValue, selectedValue, syncWithUrl, onValueChange]);

  return {
    selectedValue,
    handleSelection,
    updateUrlParam
  };
};
